import type { BlockerOption } from '@developerz/domain';
import { createEffect, createSignal, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  ANSWER_MODEL_AUTOMATIC,
  type AnswerModelOption,
  answerModelOptions,
  answerPinOf,
} from '../lib/answer-model-options';
import type { BlockerOptions, BlockerReply } from '../lib/blockers';
import type { KeyRow } from '../lib/keys-types';
import { Field } from './Field';
import { Select } from './Select';
import { buttonClass } from './ui/Button';

// The answer box for one open blocker. Presenter — it owns the draft and nothing
// else; the container makes the call, re-reads and reports the refusal.
//
// TWO SENTENCES SIT HERE RATHER THAN IN THE PAGE SUBTITLE, because this is where
// the person answering is actually looking:
//
//   - **A blocker ENDED the run, and answering starts NEW work.** The run that
//     raised this is over and its box is not re-tasked; the answer files a raw
//     task carrying both the ask and the answer, which triage decides — it may
//     become work, be cross-linked, or be dropped. A user who answers expecting a
//     resumption and sees nothing move concludes the platform broke.
//   - **Supply the LOCATION of a credential, never the credential.** This is the
//     rule the MCP schema states for the same field, for the same reason: the
//     text is durable in three places (the blocker row, the raw task's `ask`, the
//     audit row) and cannot be unsent. `missing_credential` is exactly the kind
//     of ask that invites a paste, so the warning belongs against the input.
//
// A DECISION WITH OPTIONS (#6387) is answered by picking one: a radio per choice,
// plus "Other" for an answer the choices did not foresee — a closed set with no
// way out would be worse than the textarea it replaced. The textarea appears for
// "Other", and for a choice marked `note_required` ("split it" still has to say
// which slices). The pick goes up as the choice KEY; the server writes the row's
// own label.
//
// The submit stays shut until the reply is complete — the port refuses `''`
// (`min(1)`) and a `note_required` choice with no note, so a live button there
// could only ever round-trip a 4xx.

/** The radio value of the free-text way out — not a slug, so no choice key can collide. */
const OTHER = '__other__';

/** The reply in the shape the route takes: words, a bare key, or a key plus its note. */
function replyOf(chosen: BlockerOption | null, words: string): BlockerReply {
  if (chosen === null) return { answer: words };
  return chosen.note_required ? { choice: chosen.key, note: words } : { choice: chosen.key };
}

/** The answer box for one open blocker: words, or a pick of its options plus "Other". */
export function BlockerAnswerForm(props: {
  /** Addresses the label/textarea pair — several cards render on one page. */
  readonly blockerId: string;
  /** The row's choices, or null for an ask answered in words only. */
  readonly options: BlockerOptions | null;
  /**
   * What the box asks for when the guidance steps end in a value to bring back ("Paste
   * the name you used", #6679). Model-written, so it replaces the LABEL only: the hint
   * that a credential's location goes here and never its value stays the platform's own.
   */
  readonly freeLabel?: string | undefined;
  /**
   * Offer "I have done these steps" (#6679 slice B): true when the row shows guidance
   * STEPS. It is the primary action then, and sends `{ done: true }` with whatever the box
   * holds as its note; the server writes the sentence and retries or re-judges the work.
   */
  readonly offersDone?: boolean | undefined;
  /** The account's keys, for the optional model field (#6521); absent hides the field. */
  readonly keys?: readonly KeyRow[] | undefined;
  /**
   * Does answering this leave an ask a triage judge reads (#6535)? The server's own
   * verdict, threaded from the row.
   *
   * FALSE hides the model field, because the door REFUSES a pick on those two arms: the
   * answer resumes a task that is already running, so no judge reads it and a model
   * would name something nothing uses. Until this flag existed the field was offered
   * there anyway and the person met a 422 after submitting.
   */
  readonly filesAsk: boolean;
  readonly busy: boolean;
  readonly onSubmit: (reply: BlockerReply) => void;
}): JSX.Element {
  const [draft, setDraft] = createSignal('');
  const [picked, setPicked] = createSignal<string | null>(null);
  const [model, setModel] = createSignal(ANSWER_MODEL_AUTOMATIC);
  /** The models this account can judge on, led by Automatic; empty until the keys load. */
  const models = (): readonly AnswerModelOption[] =>
    props.keys === undefined ? [] : answerModelOptions(props.keys);
  // A PICK THE FIELD NO LONGER OFFERS IS DROPPED, on EITHER axis the field is mounted on.
  //
  // `keys` is a live query: it can refetch to a shorter list, or to none at all, after a
  // model was chosen. `filesAsk` is the row's own verdict and flips the moment the task
  // behind the blocker starts running (#6535). Both unmount the field, so in both cases
  // the control the person would use to clear the pick is exactly what disappears —
  // leaving the reply carrying an `llm` nothing on screen names, which the door then
  // refuses over a model they can no longer see, let alone change.
  createEffect(() => {
    const offered = models();
    const chosen = model();
    const unmounted = !props.filesAsk || offered.length <= 1;
    if (
      chosen !== ANSWER_MODEL_AUTOMATIC &&
      (unmounted || !offered.some((o) => o.value === chosen))
    ) {
      setModel(ANSWER_MODEL_AUTOMATIC);
    }
  });
  const fieldId = (): string => `blocker-answer-${props.blockerId}`;
  /** The draft as it would be sent — whitespace alone is no answer. */
  const words = (): string => draft().trim();

  /** The picked choice, or null for none yet and for "Other". */
  const choice = () => props.options?.choices.find((c) => c.key === picked()) ?? null;
  /** The textarea shows for a words-only ask, for "Other", and for a choice that needs a note. */
  const wantsWords = (): boolean =>
    props.options === null || picked() === OTHER || choice()?.note_required === true;
  /** Whether the reply is sendable: a pick made, and words wherever words are asked for. */
  const complete = (): boolean => {
    if (props.options !== null && picked() === null) return false;
    return !wantsWords() || words() !== '';
  };

  /** Send the reply the form holds (`replyOf`). */
  const submit = (event: SubmitEvent): void => {
    event.preventDefault();
    if (!complete() || props.busy) return;
    send(replyOf(choice(), words()));
  };
  /** The done report: the box's words, if any, ride along as its note. */
  const sendDone = (): void => {
    if (!props.busy) send(words() === '' ? { done: true } : { done: true, note: words() });
  };
  // Automatic sends NO `llm`: the key is absent, never `undefined`, so the door's
  // `.strict()` body and the MCP verb read "the account's own route decides".
  const send = (reply: BlockerReply): void => {
    const pin = answerPinOf(model());
    props.onSubmit(pin === null ? reply : { ...reply, llm: pin });
  };

  return (
    <form class="field-group" onSubmit={submit}>
      <div class="alert" role="note">
        <p class="alert__desc">{t('blockers.answer_run_ended')}</p>
      </div>
      <Show when={props.options}>
        {(options) => (
          <Field as="fieldset" id={`${fieldId()}-choice`} label={t('blockers.choice_legend')}>
            {(a11y) => (
              <div class="blocker-choices">
                <For each={options().choices}>
                  {(c) => (
                    <label class="blocker-choices__option">
                      <input
                        type="radio"
                        name={a11y.id}
                        value={c.key}
                        checked={picked() === c.key}
                        disabled={props.busy}
                        onChange={() => setPicked(c.key)}
                      />
                      {c.label}
                    </label>
                  )}
                </For>
                <label class="blocker-choices__option">
                  <input
                    type="radio"
                    name={a11y.id}
                    value={OTHER}
                    checked={picked() === OTHER}
                    disabled={props.busy}
                    onChange={() => setPicked(OTHER)}
                  />
                  {t('blockers.choice_other')}
                </label>
              </div>
            )}
          </Field>
        )}
      </Show>
      <Show when={wantsWords()}>
        <Field
          label={
            choice() === null
              ? (props.freeLabel ?? t('blockers.answer_label'))
              : t('blockers.choice_note_label')
          }
          id={fieldId()}
          desc={t(choice() === null ? 'blockers.answer_hint' : 'blockers.choice_note_hint')}
        >
          {(a11y) => (
            <textarea
              {...a11y}
              class="textarea"
              rows="3"
              value={draft()}
              placeholder={choice() === null ? t('blockers.answer_placeholder') : undefined}
              disabled={props.busy}
              onInput={(event) => setDraft(event.currentTarget.value)}
            />
          )}
        </Field>
      </Show>
      {/* The judge's model for THIS answer (#6521): only where a judge actually reads the
          answer (#6535), only once the keys have loaded, and only when the account has
          something to offer. Automatic is the lead option and sends nothing. */}
      <Show when={props.filesAsk && models().length > 1}>
        <Field
          label={t('blockers.answer_model_label')}
          id={`${fieldId()}-model`}
          desc={t('blockers.answer_model_desc')}
        >
          {(a11y) => (
            <Select
              a11y={a11y}
              label={t('blockers.answer_model_label')}
              value={model()}
              options={models()}
              disabled={props.busy}
              onChange={setModel}
            />
          )}
        </Field>
      </Show>
      <Show when={props.offersDone}>
        <button
          type="button"
          class={buttonClass({ size: 'sm' })}
          disabled={props.busy}
          onClick={sendDone}
        >
          {t('blockers.guidance_done')}
        </button>
      </Show>
      <button
        type="submit"
        class={buttonClass({ size: 'sm', variant: props.offersDone ? 'outline' : 'primary' })}
        disabled={props.busy || !complete()}
      >
        {t(props.busy ? 'blockers.answer_sending' : 'blockers.answer_submit')}
      </button>
    </form>
  );
}
